import { useState } from 'react'
import {
  PlanStep,
  PlanFormation,
  PlanCasing,
  PlanPerforation,
  PlanVerificationPayload,
} from '../../lib/api/w3-wizard'

// ---- Props ----

interface PlanVerificationProps {
  payload: {
    well_header: Record<string, string | number | boolean | null>
    steps: PlanStep[]
    formations: PlanFormation[]
    casing_record: PlanCasing[]
    existing_perforations: PlanPerforation[]
  }
  onConfirm: (corrected: PlanVerificationPayload) => Promise<void>
  onReupload: () => void
}

// ---- Step type options ----

const STEP_TYPE_OPTIONS = [
  'cement_plug',
  'bridge_plug',
  'surface_plug',
  'squeeze',
  'spot_plug',
  'cibp',
  'other',
]

// ---- Casing string type options ----

const CASING_TYPE_OPTIONS = [
  'surface',
  'intermediate',
  'production',
  'liner',
  'conductor',
]

// ---- Perforation status options ----

const PERF_STATUS_OPTIONS = ['open', 'squeezed', 'plugged']

// ---- Well header fields to display ----

const HEADER_FIELDS = [
  'api_number',
  'operator',
  'well_name',
  'county',
  'state',
  'field',
  'total_depth',
  'elevation_ft',
]

// ---- Helpers ----

function parseNum(val: string): number | null {
  const n = parseFloat(val)
  return isNaN(n) ? null : n
}

function parseIntVal(val: string): number {
  const n = Number.parseInt(val, 10)
  return isNaN(n) ? 0 : n
}

// ---- Collapsible section ----

interface SectionProps {
  title: string
  count: number
  children: React.ReactNode
  defaultOpen?: boolean
}

function Section({ title, count, children, defaultOpen = true }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="rounded-xl border border-gray-200 dark:border-white/[0.08] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 dark:bg-white/[0.03] hover:bg-gray-100 dark:hover:bg-white/[0.05] transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-800 dark:text-white/90">{title}</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600 dark:bg-white/[0.1] dark:text-gray-300">
            {count} {count === 1 ? 'item' : 'items'}
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  )
}

// ---- Table helpers ----

interface ThProps {
  children?: React.ReactNode
  className?: string
}

function Th({ children, className = '' }: ThProps) {
  return (
    <th
      className={`px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap ${className}`}
    >
      {children}
    </th>
  )
}

interface TdProps {
  children: React.ReactNode
  className?: string
}

function Td({ children, className = '' }: TdProps) {
  return (
    <td className={`px-3 py-2 align-middle ${className}`}>
      {children}
    </td>
  )
}

const inputCls =
  'w-full rounded border border-gray-200 dark:border-white/[0.1] bg-white dark:bg-white/[0.05] px-2 py-1 text-sm text-gray-800 dark:text-white/90 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500'

const selectCls =
  'w-full rounded border border-gray-200 dark:border-white/[0.1] bg-white dark:bg-white/[0.05] px-2 py-1 text-sm text-gray-800 dark:text-white/90 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500'

// ---- Delete row button ----

function DeleteBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
      title="Remove row"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  )
}

// ---- Add row button ----

function AddRowBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-2 flex items-center gap-1.5 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
      </svg>
      {label}
    </button>
  )
}

// ---- Main component ----

export default function PlanVerification({ payload, onConfirm, onReupload }: PlanVerificationProps) {
  const [wellHeader, setWellHeader] = useState<Record<string, string | number | boolean | null>>(
    payload.well_header ?? {}
  )
  const [steps, setSteps] = useState<PlanStep[]>(payload.steps ?? [])
  const [formations, setFormations] = useState<PlanFormation[]>(payload.formations ?? [])
  const [casingRecord, setCasingRecord] = useState<PlanCasing[]>(payload.casing_record ?? [])
  const [perforations, setPerforations] = useState<PlanPerforation[]>(payload.existing_perforations ?? [])
  const [confirming, setConfirming] = useState(false)

  // ---- Header handlers ----

  const setHeaderField = (key: string, val: string) => {
    setWellHeader((prev) => ({ ...prev, [key]: val }))
  }

  // ---- Steps handlers ----

  const updateStep = (idx: number, field: keyof PlanStep, val: string) => {
    setSteps((prev) => {
      const next = [...prev]
      const row = { ...next[idx] }
      if (field === 'step_number') {
        row.step_number = parseIntVal(val)
      } else if (field === 'depth_top_ft' || field === 'depth_bottom_ft' || field === 'sacks') {
        row[field] = parseNum(val)
      } else {
        ;(row as Record<string, string | number | null>)[field] = val
      }
      next[idx] = row
      return next
    })
  }

  const addStep = () => {
    setSteps((prev) => [
      ...prev,
      {
        step_number: prev.length + 1,
        step_type: 'cement_plug',
        depth_top_ft: null,
        depth_bottom_ft: null,
        sacks: null,
        cement_class: null,
        description: null,
        category: 'plug',
      },
    ])
  }

  const removeStep = (idx: number) => {
    setSteps((prev) => {
      const next = prev.filter((_, i) => i !== idx)
      return next.map((s, i) => ({ ...s, step_number: i + 1 }))
    })
  }

  // ---- Formation handlers ----

  const updateFormation = (idx: number, field: keyof PlanFormation, val: string) => {
    setFormations((prev) => {
      const next = [...prev]
      const row = { ...next[idx] }
      if (field === 'top_ft') {
        row.top_ft = parseNum(val)
      } else {
        row.formation_name = val
      }
      next[idx] = row
      return next
    })
  }

  const addFormation = () => {
    setFormations((prev) => [...prev, { formation_name: '', top_ft: null }])
  }

  const removeFormation = (idx: number) => {
    setFormations((prev) => prev.filter((_, i) => i !== idx))
  }

  // ---- Casing handlers ----

  const updateCasing = (idx: number, field: keyof PlanCasing, val: string) => {
    setCasingRecord((prev) => {
      const next = [...prev]
      const row = { ...next[idx] }
      const numericFields: (keyof PlanCasing)[] = [
        'size_in',
        'weight_ppf',
        'top_ft',
        'bottom_ft',
        'shoe_depth_ft',
        'hole_size_in',
        'cement_top_ft',
      ]
      if (numericFields.includes(field)) {
        ;(row as Record<string, number | null | string>)[field] = parseNum(val)
      } else {
        ;(row as Record<string, string | null>)[field] = val
      }
      next[idx] = row
      return next
    })
  }

  const addCasing = () => {
    setCasingRecord((prev) => [
      ...prev,
      {
        string_type: 'surface',
        size_in: null,
        weight_ppf: null,
        grade: null,
        top_ft: null,
        bottom_ft: null,
        shoe_depth_ft: null,
        hole_size_in: null,
        cement_top_ft: null,
      },
    ])
  }

  const removeCasing = (idx: number) => {
    setCasingRecord((prev) => prev.filter((_, i) => i !== idx))
  }

  // ---- Perforation handlers ----

  const updatePerforation = (idx: number, field: keyof PlanPerforation, val: string) => {
    setPerforations((prev) => {
      const next = [...prev]
      const row = { ...next[idx] }
      if (field === 'depth_top_ft' || field === 'depth_bottom_ft') {
        row[field] = parseNum(val)
      } else {
        ;(row as Record<string, string | null>)[field] = val
      }
      next[idx] = row
      return next
    })
  }

  const addPerforation = () => {
    setPerforations((prev) => [
      ...prev,
      { depth_top_ft: null, depth_bottom_ft: null, formation_name: null, status: 'open' },
    ])
  }

  const removePerforation = (idx: number) => {
    setPerforations((prev) => prev.filter((_, i) => i !== idx))
  }

  // ---- Confirm handler ----

  const handleConfirm = async () => {
    setConfirming(true)
    try {
      await onConfirm({
        well_header: wellHeader,
        steps,
        formations,
        casing_record: casingRecord,
        existing_perforations: perforations,
      })
    } finally {
      setConfirming(false)
    }
  }

  // ---- Render ----

  return (
    <div className="rounded-2xl border border-amber-200 dark:border-amber-800/40 bg-white dark:bg-white/[0.03] overflow-hidden">
      {/* Header */}
      <div className="border-b border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 px-6 py-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="text-base font-semibold text-amber-900 dark:text-amber-200">
              Verify Imported Plan Data
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
              Review the data extracted from the approved plan. Correct any errors before proceeding.
              Ticket parsing will begin after verification.
            </p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="p-6 space-y-4">

        {/* Well Header */}
        <Section title="Well Header" count={HEADER_FIELDS.filter((f) => f in wellHeader).length}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {HEADER_FIELDS.map((field) => (
              <div key={field}>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 capitalize">
                  {field.replace(/_/g, ' ')}
                </label>
                <input
                  type="text"
                  className={inputCls}
                  value={wellHeader[field] != null ? String(wellHeader[field]) : ''}
                  onChange={(e) => setHeaderField(field, e.target.value)}
                />
              </div>
            ))}
          </div>
        </Section>

        {/* P&A Steps */}
        <Section title="P&A Steps" count={steps.length}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-white/[0.04]">
                  <Th className="w-12">Step #</Th>
                  <Th>Type</Th>
                  <Th>Top (ft)</Th>
                  <Th>Bottom (ft)</Th>
                  <Th>Sacks</Th>
                  <Th>Cement Class</Th>
                  <Th>Description</Th>
                  <Th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                {steps.map((step, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/60 dark:hover:bg-white/[0.02]">
                    <Td>
                      <input
                        type="number"
                        className={inputCls}
                        value={step.step_number}
                        onChange={(e) => updateStep(idx, 'step_number', e.target.value)}
                        style={{ width: '52px' }}
                      />
                    </Td>
                    <Td>
                      <select
                        className={selectCls}
                        value={step.step_type}
                        onChange={(e) => updateStep(idx, 'step_type', e.target.value)}
                      >
                        {STEP_TYPE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </Td>
                    <Td>
                      <input
                        type="number"
                        className={inputCls}
                        placeholder="—"
                        value={step.depth_top_ft ?? ''}
                        onChange={(e) => updateStep(idx, 'depth_top_ft', e.target.value)}
                        style={{ width: '80px' }}
                      />
                    </Td>
                    <Td>
                      <input
                        type="number"
                        className={inputCls}
                        placeholder="—"
                        value={step.depth_bottom_ft ?? ''}
                        onChange={(e) => updateStep(idx, 'depth_bottom_ft', e.target.value)}
                        style={{ width: '80px' }}
                      />
                    </Td>
                    <Td>
                      <input
                        type="number"
                        className={inputCls}
                        placeholder="—"
                        value={step.sacks ?? ''}
                        onChange={(e) => updateStep(idx, 'sacks', e.target.value)}
                        style={{ width: '70px' }}
                      />
                    </Td>
                    <Td>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="—"
                        value={step.cement_class ?? ''}
                        onChange={(e) => updateStep(idx, 'cement_class', e.target.value)}
                        style={{ width: '90px' }}
                      />
                    </Td>
                    <Td>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="—"
                        value={step.description ?? ''}
                        onChange={(e) => updateStep(idx, 'description', e.target.value)}
                        style={{ minWidth: '140px' }}
                      />
                    </Td>
                    <Td>
                      <DeleteBtn onClick={() => removeStep(idx)} />
                    </Td>
                  </tr>
                ))}
                {steps.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-center text-sm text-gray-400 dark:text-gray-500">
                      No steps — add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <AddRowBtn onClick={addStep} label="Add Step" />
        </Section>

        {/* Formations */}
        <Section title="Formations" count={formations.length} defaultOpen={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-white/[0.04]">
                  <Th>Formation Name</Th>
                  <Th>Top (ft)</Th>
                  <Th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                {formations.map((f, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/60 dark:hover:bg-white/[0.02]">
                    <Td>
                      <input
                        type="text"
                        className={inputCls}
                        value={f.formation_name}
                        onChange={(e) => updateFormation(idx, 'formation_name', e.target.value)}
                      />
                    </Td>
                    <Td>
                      <input
                        type="number"
                        className={inputCls}
                        placeholder="—"
                        value={f.top_ft ?? ''}
                        onChange={(e) => updateFormation(idx, 'top_ft', e.target.value)}
                        style={{ width: '90px' }}
                      />
                    </Td>
                    <Td>
                      <DeleteBtn onClick={() => removeFormation(idx)} />
                    </Td>
                  </tr>
                ))}
                {formations.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-3 py-4 text-center text-sm text-gray-400 dark:text-gray-500">
                      No formations — add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <AddRowBtn onClick={addFormation} label="Add Formation" />
        </Section>

        {/* Casing Record */}
        <Section title="Casing Record" count={casingRecord.length} defaultOpen={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-white/[0.04]">
                  <Th>Type</Th>
                  <Th>Size (in)</Th>
                  <Th>Weight (ppf)</Th>
                  <Th>Grade</Th>
                  <Th>Top (ft)</Th>
                  <Th>Bottom (ft)</Th>
                  <Th>Shoe Depth (ft)</Th>
                  <Th>Cement Top (ft)</Th>
                  <Th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                {casingRecord.map((c, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/60 dark:hover:bg-white/[0.02]">
                    <Td>
                      <select
                        className={selectCls}
                        value={c.string_type}
                        onChange={(e) => updateCasing(idx, 'string_type', e.target.value)}
                      >
                        {CASING_TYPE_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </Td>
                    {(['size_in', 'weight_ppf'] as const).map((field) => (
                      <Td key={field}>
                        <input
                          type="number"
                          className={inputCls}
                          placeholder="—"
                          value={c[field] ?? ''}
                          onChange={(e) => updateCasing(idx, field, e.target.value)}
                          style={{ width: '70px' }}
                        />
                      </Td>
                    ))}
                    <Td>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="—"
                        value={c.grade ?? ''}
                        onChange={(e) => updateCasing(idx, 'grade', e.target.value)}
                        style={{ width: '70px' }}
                      />
                    </Td>
                    {(['top_ft', 'bottom_ft', 'shoe_depth_ft', 'cement_top_ft'] as const).map((field) => (
                      <Td key={field}>
                        <input
                          type="number"
                          className={inputCls}
                          placeholder="—"
                          value={c[field] ?? ''}
                          onChange={(e) => updateCasing(idx, field, e.target.value)}
                          style={{ width: '80px' }}
                        />
                      </Td>
                    ))}
                    <Td>
                      <DeleteBtn onClick={() => removeCasing(idx)} />
                    </Td>
                  </tr>
                ))}
                {casingRecord.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-4 text-center text-sm text-gray-400 dark:text-gray-500">
                      No casing records — add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <AddRowBtn onClick={addCasing} label="Add Casing String" />
        </Section>

        {/* Perforations */}
        <Section title="Existing Perforations" count={perforations.length} defaultOpen={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-white/[0.04]">
                  <Th>Top (ft)</Th>
                  <Th>Bottom (ft)</Th>
                  <Th>Formation</Th>
                  <Th>Status</Th>
                  <Th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                {perforations.map((p, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/60 dark:hover:bg-white/[0.02]">
                    <Td>
                      <input
                        type="number"
                        className={inputCls}
                        placeholder="—"
                        value={p.depth_top_ft ?? ''}
                        onChange={(e) => updatePerforation(idx, 'depth_top_ft', e.target.value)}
                        style={{ width: '90px' }}
                      />
                    </Td>
                    <Td>
                      <input
                        type="number"
                        className={inputCls}
                        placeholder="—"
                        value={p.depth_bottom_ft ?? ''}
                        onChange={(e) => updatePerforation(idx, 'depth_bottom_ft', e.target.value)}
                        style={{ width: '90px' }}
                      />
                    </Td>
                    <Td>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="—"
                        value={p.formation_name ?? ''}
                        onChange={(e) => updatePerforation(idx, 'formation_name', e.target.value)}
                      />
                    </Td>
                    <Td>
                      <select
                        className={selectCls}
                        value={p.status}
                        onChange={(e) => updatePerforation(idx, 'status', e.target.value)}
                      >
                        {PERF_STATUS_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </Td>
                    <Td>
                      <DeleteBtn onClick={() => removePerforation(idx)} />
                    </Td>
                  </tr>
                ))}
                {perforations.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-sm text-gray-400 dark:text-gray-500">
                      No perforations — add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <AddRowBtn onClick={addPerforation} label="Add Perforation" />
        </Section>
      </div>

      {/* Bottom actions */}
      <div className="border-t border-gray-200 dark:border-white/[0.05] px-6 py-4 flex items-center justify-between">
        <button
          type="button"
          onClick={onReupload}
          className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          Re-upload Plan
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={confirming}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
        >
          {confirming ? (
            <>
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
              Confirming...
            </>
          ) : (
            'Confirm Plan Data'
          )}
        </button>
      </div>
    </div>
  )
}
